import React, {useMemo} from 'react';
import {
    Box, CardContent, IconButton, Typography,
    Tooltip, Divider, Skeleton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommunityUserLink from "./ProfileLink";
import {useAppDispatch, useAppSelector} from "../../../hooks/hooks";
import moment from "moment/moment";
import {asyncToggleTourLike} from "../../../store/Vanlifer/tourSlice";
import {Story} from "../../../services/restserver-openapi";

interface TourCardContentProps {
    onEditClick: () => void;
    onEditLinkClick: () => void;
    showSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

export default function TourSidebarContent({
                                            onEditClick,
                                            onEditLinkClick,
                                            showSnackbar
                                        }: TourCardContentProps) {
    const dispatch = useAppDispatch();
    const chosenTourDetail = useAppSelector(state => state.tour.choosedTourDetail);
    const loading = useAppSelector(state => state.tour.loading);
    const isAuthenticated = useAppSelector((state) => state.login.isAuthenticated);
    const detailedTourDistances = useAppSelector(state => state.tour.detailedTourDistances);
    const vanliferId = useAppSelector(state => state.user.vanliferId);
    const userEntitlements = useAppSelector(state => state.user.entitlements);

    const canCreateContent = useMemo(() => {
        return userEntitlements.some(entitlement => entitlement.featureId === 'vanlifercreatecontent' && entitlement.value === "true")
    }, [userEntitlements]);

    const isOwner = (isAuthenticated && chosenTourDetail.vanliferId === vanliferId) ?? false;

    const isLiked = chosenTourDetail.liked || false;

    const { oldestFromDate, newestToDate, numberOfDays } = useMemo(() => {
        if (!chosenTourDetail.stays?.length) {
            return { oldestFromDate: null, newestToDate: null, numberOfDays: null };
        }

        const oldestStay = chosenTourDetail.stays.reduce((oldest, stay) =>
            oldest.fromDate! < stay.fromDate! ? oldest : stay
        );
        const newestStay = chosenTourDetail.stays.reduce((newest, stay) =>
            newest.toDate! > stay.toDate! ? newest : stay
        );

        const oldestFromDate = oldestStay.fromDate;
        const newestToDate = newestStay.toDate;

        let numberOfDays: number | null = null;
        if (oldestFromDate && newestToDate) {
            const from = new Date(oldestFromDate);
            const to = new Date(newestToDate);
            const diffTime = to.getTime() - from.getTime();
            numberOfDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }

        return { oldestFromDate, newestToDate, numberOfDays };
    }, [chosenTourDetail.stays]);

    const handleLike = async () => {
        try {
            await dispatch(asyncToggleTourLike({
                tourId: chosenTourDetail.tourId!,
                isLiked: !isLiked
            })).unwrap();
        } catch (e) {
            showSnackbar('Liken fehlgeschlagen – bitte versuche es erneut', 'error');
        }
    };

    return (
        <CardContent sx={{ p: 1, pt: 2 }}>
            {/* Dates and User */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {loading ? (
                    <Skeleton width="60%" />
                ) : (
                    <>
                        {moment(oldestFromDate).format('DD.MM.YYYY')} - {moment(newestToDate).format('DD.MM.YYYY')} ·{' '}
                        <CommunityUserLink
                            communityName={chosenTourDetail.owner ?? "Unbekannter User"}
                        />
                    </>
                )}
            </Typography>

            {/* Title and Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                {loading ? (
                    <Skeleton width="50%" height={32} />
                ) : (
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {chosenTourDetail.name!}
                    </Typography>
                )}
                {!loading && isAuthenticated && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isOwner && (
                            <>
                                <Tooltip title={canCreateContent ? "Reisedetails bearbeiten" : "Fehlende Berechtigung"}>
                                    <span>
                                        <IconButton size="small" onClick={onEditClick} disabled={!canCreateContent}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Tour Teilen">
                                    <IconButton size="small" onClick={onEditLinkClick}>
                                        <ShareIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip
                            title={
                                chosenTourDetail.sharingMode === Story.sharingMode.PRIVATE
                                    ? "Liken ist nur bei öffentlichen Touren möglich"
                                    : isLiked
                                        ? "Like entfernen"
                                        : "Like hinzufügen"
                            }
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleLike}
                                    disabled={chosenTourDetail.sharingMode === Story.sharingMode.PRIVATE}
                                >
                                    <FavoriteIcon
                                        fontSize="small"
                                        sx={{
                                            color:
                                                isLiked
                                                    ? chosenTourDetail.sharingMode === Story.sharingMode.PRIVATE
                                                        ? '#ff9999' // muted red for private
                                                        : 'red'     // standard red for public
                                                    : 'inherit'
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Description */}
            <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
            >
                {loading ? (
                    <>
                        <Skeleton width="100%" />
                        <Skeleton width="80%" />
                        <Skeleton width="50%" />
                    </>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                        {chosenTourDetail.description}
                    </Typography>
                )}
            </Typography>

            {/* Statistics */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                {[0, 1, 2, 3].map(index => (
                    <React.Fragment key={index}>
                        <Box
                            sx={{
                                flex: 1,
                                px: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {loading ? (
                                    <Skeleton width="50%" />
                                ) : (
                                    [
                                        chosenTourDetail.stays?.length,
                                        numberOfDays,
                                        Math.round((detailedTourDistances?.reduce((acc, val) => acc + val, 0)) || 0),
                                        chosenTourDetail.likeCount
                                    ][index]
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {['Stopps', 'Tage', 'km', 'Likes'][index]}
                            </Typography>
                        </Box>
                        {index < 3 && <Divider orientation="vertical" flexItem />}
                    </React.Fragment>
                ))}
            </Box>
        </CardContent>
    );
}
